import * as React from 'react';
import { css, CSSObject, SerializedStyles } from '@emotion/react';
import { ColorOption, IconSize, colorToCssValue } from '../../theming';
import { Styler, StylerProps } from '../Styler';
import { WithRef } from '../../utils/withRef';

// Static styles
const STATIC_STYLES: SerializedStyles = css`
    fill: var(--color);
    width: var(--size);
    height: var(--size);
    vertical-align: middle;
    /* Default transform (can be overridden or combined) */
    transform: var(--bi-directional-transform, none);

    &[data-bi-directional]:dir(rtl) {
        /* Only apply the flip in RTL */
        --bi-directional-transform: rotateY(180deg);
    }
`;
export interface SvgIconBaseProps extends StylerProps {
    /**
     * If `true`, the icon will be mirrored on the x-axis when the document direction is right-to-left.
     * This property will typically be used for icons which communicate direction or time.
     */
    biDirectional?: boolean;

    /**
     * The colour variant of the component.
     */
    color?: ColorOption;

    /**
     * The size of the component.
     * @default m
     */
    size?: IconSize;

    /**
     * The viewBox attribute applied to the svg root node.
     */
    viewBox?: string;
}

export interface SvgIconProps extends SvgIconBaseProps {
    /**
     * The svg content.
     */
    children: React.ReactNode;
}

export function SvgIcon(props: WithRef<'svg', SvgIconProps>): React.JSX.Element {
    const { biDirectional = false, children, color, size = 'm', viewBox = '0 0 24 24', ref, ...other } = props;

    // Prepare CSS variables
    const cssVariables: CSSObject = React.useMemo(() => {
        return {
            '--size': `var(--icon-size-${size})`,
            '--color': color ? colorToCssValue(color) : 'currentColor',
        };
    }, [color, size]);

    return (
        <Styler
            ref={ref}
            component="svg"
            style={cssVariables}
            baseStyles={[STATIC_STYLES]}
            viewBox={viewBox}
            xmlns="http://www.w3.org/2000/svg"
            data-component="svg-icon"
            data-bi-directional={biDirectional || undefined}
            role="presentation"
            {...other}
        >
            {children}
        </Styler>
    );
}
